import * as THREE from 'three';
import type { Track } from '@/world/Track';
import { moverPoseAt, type PlacedMover } from '@/physics/movers';
import { attachPropModel, BUS, DANFO, NYC_TAXI, RICKSHAW, TOKYO_TAXI, vespaStyle, type PropStyle } from '@/world/scenery/propModels';
import {
  buildVespaProp,
  doubleDeckerCanvases,
  makeRoadWheel,
  paintedBoxMaterials,
  propHash,
  propTexture,
  roundedBoxGeo,
  taperedGeo,
  earFinGeo,
  furMottleTexture,
  toneJitter,
  vehicleFrontCanvas,
  vehicleRoofCanvas,
  vehicleSideCanvas,
} from '@/world/scenery/propKit';

/**
 * MoversView.ts — draws the M99 moving obstacles (crossing city traffic). The
 * SIM owns the timetable (physics/movers.ts, a pure function of the race
 * clock); this just poses one themed mesh group per mover every frame — the
 * same split as MudView/FreeKickWallView. Each style is a compact build in the
 * game's low-poly language (GS4: painted-canvas bodywork + real wheels + roof
 * caps from the shared propKit — the chase cam looks DOWN at movers, so the
 * roof and flanks are the primary read), with the moving parts animated from
 * DISTANCE TRAVELLED (wheels spin, legs swing, the beach ball rolls + hops) so
 * everything freezes naturally while a mover dwells at the kerb.
 */

/** Deterministic 0..1 hash for canvas art (never the layout RNG). */
const art = propHash;

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d') as CanvasRenderingContext2D];
}

function canvasTex(c: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Soft radial contact-shadow (shared by every mover — grounds the mesh). */
function shadowTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 64);
  const g = x.createRadialGradient(32, 32, 4, 32, 32, 30);
  g.addColorStop(0, 'rgba(0,0,0,0.42)');
  g.addColorStop(0.7, 'rgba(0,0,0,0.25)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, 64, 64);
  return canvasTex(c);
}

/** Two-row black/white checker band (NYC cab). */
function checkerTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(64, 16);
  x.fillStyle = '#f5f2e8';
  x.fillRect(0, 0, 64, 16);
  x.fillStyle = '#15161a';
  for (let col = 0; col < 8; col++)
    for (let row = 0; row < 2; row++) if ((col + row) % 2 === 0) x.fillRect(col * 8, row * 8, 8, 8);
  return canvasTex(c);
}

/** Classic 6-gore beach ball: alternating colour segments around the sphere. */
function beachBallTexture(): THREE.CanvasTexture {
  const [c, x] = makeCanvas(192, 96);
  const cols = ['#ff4438', '#ffffff', '#ffd21c', '#ffffff', '#2288ee', '#ffffff'];
  const w = 192 / cols.length;
  cols.forEach((col, i) => {
    x.fillStyle = col;
    x.fillRect(i * w, 0, w + 1, 96);
  });
  return canvasTex(c);
}

interface MoverKit {
  shadow: THREE.MeshBasicMaterial;
  glass: THREE.MeshStandardMaterial;
  dark: THREE.MeshStandardMaterial;
  chrome: THREE.MeshStandardMaterial;
  headlamp: THREE.MeshBasicMaterial;
  taillamp: THREE.MeshBasicMaterial;
  checker: THREE.MeshStandardMaterial;
}

let kit: MoverKit | null = null;

function makeKit(): MoverKit {
  return {
    shadow: new THREE.MeshBasicMaterial({
      map: shadowTexture(),
      transparent: true,
      depthWrite: false,
    }),
    glass: new THREE.MeshStandardMaterial({ color: 0x1a2028, roughness: 0.18, flatShading: true }),
    dark: new THREE.MeshStandardMaterial({ color: 0x181a1f, roughness: 0.85, flatShading: true }),
    chrome: new THREE.MeshStandardMaterial({ color: 0xd9dde4, roughness: 0.22, metalness: 0.9 }),
    headlamp: new THREE.MeshBasicMaterial({ color: 0xffe9b0 }),
    taillamp: new THREE.MeshBasicMaterial({ color: 0xff3540 }),
    checker: new THREE.MeshStandardMaterial({ map: checkerTexture(), roughness: 0.5 }),
  };
}

const std = (color: number, rough = 0.6): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color, roughness: rough, flatShading: true });

/** Smooth-shaded standard material (the animal builds — flat shading is what
 *  made them read as voxels). */
const smooth = (color: THREE.ColorRepresentation, rough = 0.85): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color, roughness: rough });

function box(
  w: number,
  h: number,
  d: number,
  mat: THREE.Material | THREE.Material[],
  x = 0,
  y = 0,
  z = 0,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  return m;
}

/** A wheel spun about its axle by the shared `spin` (radians). Wheels lie with
 *  the cylinder axis along local X (the axle), fronting the +Z travel axis. */
interface Spinner {
  mesh: THREE.Object3D;
  radius: number;
}

/** One built mover: root group + the animated parts. */
export interface MoverRig {
  root: THREE.Group;
  wheels: Spinner[];
  /** Leg pivots (animals): swung by gait phase; sign alternates per leg. */
  legs: { pivot: THREE.Object3D; sign: number }[];
  /** The beach ball body (rolls + hops), if this rig is one. */
  ball?: THREE.Mesh;
  ballRadius?: number;
  /** Necks/heads that bob while walking (animals). */
  bobbers: THREE.Object3D[];
}

function newRig(): MoverRig {
  return { root: new THREE.Group(), wheels: [], legs: [], bobbers: [] };
}

function addShadow(rig: MoverRig, w: number, d: number): void {
  const k = (kit ??= makeKit());
  const s = new THREE.Mesh(new THREE.PlaneGeometry(w, d), k.shadow);
  s.rotation.x = -Math.PI / 2;
  s.position.y = 0.04;
  s.renderOrder = 1;
  s.userData.keepOnSwap = true; // M158: stays when a model replaces the stand-in
  rig.root.add(s);
}

/** A propKit road wheel parked at (x, r, z), registered for the shared spin. */
function addWheel(rig: MoverRig, x: number, z: number, r: number, w: number, hub?: number): void {
  const wheel = makeRoadWheel(r, w, hub === undefined ? undefined : { hub });
  wheel.pivot.position.set(x, r, z);
  rig.root.add(wheel.pivot);
  rig.wheels.push({ mesh: wheel.pivot, radius: r });
}

// ---- Vehicle builds (front toward local +Z, the §6 mesh convention) ---------

/** Shared sedan body (NYC cab / Tokyo kei-cab): hood → glasshouse → trunk,
 *  with real hub-capped wheels, dark arch panels behind them, a painted roof
 *  cap (the chase-cam face) and a grille between the lamps. */
function sedan(
  rig: MoverRig,
  paint: THREE.Material,
  opts: {
    roofSign?: THREE.Material;
    roofPaint: string;
    glass?: THREE.Material;
    hub?: number;
  },
): void {
  const k = (kit ??= makeKit());
  const glass = opts.glass ?? k.glass;
  const roofMat = new THREE.MeshStandardMaterial({
    map: propTexture(vehicleRoofCanvas({ w: 64, h: 128, paint: opts.roofPaint, seams: 2 })),
    roughness: 0.5,
  });
  rig.root.add(
    box(1.7, 0.5, 3.6, paint, 0, 0.55, 0), // main body
    box(1.6, 0.34, 1.1, paint, 0, 0.92, 0.9), // hood
    box(1.5, 0.52, 1.6, glass, 0, 1.05, -0.15), // glasshouse
    box(1.56, 0.1, 1.7, roofMat, 0, 1.33, -0.15), // roof cap
    box(1.62, 0.14, 0.24, k.chrome, 0, 0.4, 1.78), // front bumper
    box(1.62, 0.14, 0.24, k.chrome, 0, 0.4, -1.78), // rear bumper
    box(0.5, 0.16, 0.05, k.dark, 0, 0.72, 1.81), // grille
    box(0.34, 0.12, 0.06, k.headlamp, -0.55, 0.72, 1.81),
    box(0.34, 0.12, 0.06, k.headlamp, 0.55, 0.72, 1.81),
    box(0.34, 0.12, 0.06, k.taillamp, -0.55, 0.72, -1.81),
    box(0.34, 0.12, 0.06, k.taillamp, 0.55, 0.72, -1.81),
  );
  // Dark arch panels flush with the flanks — the cutout contrast that makes
  // the wheels read instead of vanishing tyre-dark against road-dark.
  for (const sx of [-1, 1])
    for (const z of [1.15, -1.15]) rig.root.add(box(0.06, 0.66, 1.0, k.dark, sx * 0.84, 0.42, z));
  if (opts.roofSign) rig.root.add(box(0.7, 0.26, 0.5, opts.roofSign, 0, 1.51, -0.15));
  addWheel(rig, -0.88, 1.15, 0.4, 0.28, opts.hub);
  addWheel(rig, 0.88, 1.15, 0.4, 0.28, opts.hub);
  addWheel(rig, -0.88, -1.15, 0.4, 0.28, opts.hub);
  addWheel(rig, 0.88, -1.15, 0.4, 0.28, opts.hub);
  addShadow(rig, 2.6, 4.4);
}

function buildCab(): MoverRig {
  const k = (kit ??= makeKit());
  const rig = newRig();
  sedan(rig, std(0xf7b81c, 0.5), { roofSign: k.headlamp, roofPaint: '#e2a714', hub: 0xb9bfc8 });
  // The NYC checker band down each flank.
  const band = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 0.26), k.checker);
  band.position.set(-0.86, 0.62, 0);
  band.rotation.y = -Math.PI / 2;
  const band2 = band.clone();
  band2.position.x = 0.86;
  band2.rotation.y = Math.PI / 2;
  rig.root.add(band, band2);
  swapInModel(rig, NYC_TAXI);
  return rig;
}

/** M158: a hand-built prop model replaces the procedural stand-in once it
 *  loads, and the rig's wheel spin moves onto the model's wheels. */
function swapInModel(rig: MoverRig, style: PropStyle): void {
  attachPropModel(rig.root, style, (wheels) => {
    rig.wheels.length = 0;
    rig.wheels.push(...wheels);
  });
}

function buildKeicab(): MoverRig {
  const rig = newRig();
  // Tokyo night cab: near-black SPECULAR body, warm lit glasshouse (the cabin
  // light is what sells a night cab), light hubcaps, hot roof sign.
  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0x14171e,
    roughness: 0.26,
    metalness: 0.35,
    flatShading: true,
  });
  const litGlass = new THREE.MeshStandardMaterial({
    color: 0x2a3542,
    roughness: 0.14,
    emissive: 0xffd98c,
    emissiveIntensity: 0.5,
  });
  sedan(rig, bodyMat, {
    roofSign: new THREE.MeshBasicMaterial({ color: 0x2ee6a8 }),
    roofPaint: '#1b1f28',
    glass: litGlass,
    hub: 0xc8cdd4,
  });
  swapInModel(rig, TOKYO_TAXI);
  return rig;
}

function buildBus(): MoverRig {
  const k = (kit ??= makeKit());
  const rig = newRig();
  // GS4: the double-decker is a PAINTED BOX (both lit window decks, cream
  // band, route boards, dark arches) wearing the shared propKit canvas set —
  // the same art the parked buses + the London bus-ramp reuse.
  const set = doubleDeckerCanvases();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(2.4, 3.75, 7.2),
    paintedBoxMaterials({ ...set, roughness: 0.5, emissiveIntensity: 0.5 }),
  );
  body.position.y = 2.12;
  rig.root.add(body);
  rig.root.add(
    box(2.44, 0.16, 0.3, k.dark, 0, 0.42, 3.66), // front bumper
    box(2.44, 0.16, 0.3, k.dark, 0, 0.42, -3.66), // rear bumper
    box(0.4, 0.15, 0.07, k.headlamp, -0.75, 0.92, 3.72), // 3D lamps over the paint
    box(0.4, 0.15, 0.07, k.headlamp, 0.75, 0.92, 3.72),
    box(0.4, 0.15, 0.07, k.taillamp, -0.75, 0.92, -3.72),
    box(0.4, 0.15, 0.07, k.taillamp, 0.75, 0.92, -3.72),
  );
  // Real glass planes proud of the paint for specular catch (driver + top deck).
  for (const y of [1.95, 3.35]) {
    const g = new THREE.Mesh(new THREE.PlaneGeometry(2.0, 0.62), k.glass);
    g.position.set(0, y, 3.62);
    rig.root.add(g);
  }
  // Wing mirrors.
  for (const sx of [-1, 1]) {
    const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.5, 5), k.dark);
    stalk.rotation.z = Math.PI / 2;
    stalk.position.set(sx * 1.35, 2.6, 3.5);
    rig.root.add(stalk, box(0.08, 0.34, 0.2, k.dark, sx * 1.56, 2.6, 3.5));
  }
  addWheel(rig, -1.06, 2.3, 0.5, 0.34, 0x565b63);
  addWheel(rig, 1.06, 2.3, 0.5, 0.34, 0x565b63);
  addWheel(rig, -1.06, -2.3, 0.5, 0.34, 0x565b63);
  addWheel(rig, 1.06, -2.3, 0.5, 0.34, 0x565b63);
  addShadow(rig, 3.4, 8.2);
  swapInModel(rig, BUS);
  return rig;
}

function buildDanfo(): MoverRig {
  const k = (kit ??= makeKit());
  const rig = newRig();
  // Lagos danfo: battered yellow minibus — painted box with the iconic BLACK
  // side stripe, window row, heavy rust grime, chrome bumpers + a loaded rack.
  const yellow = '#e6b81a';
  const side = vehicleSideCanvas({
    w: 256,
    h: 128,
    paint: yellow,
    skirt: '#6b5410',
    bands: [{ top: 0.14, height: 0.3, panes: 5, lit: 0.45, warm: true }],
    stripes: [{ y: 0.52, h: 0.14, color: '#15161a' }],
    grime: 0.55,
    arches: [0.17, 0.83],
    archR: 0.18,
    seed: 21,
  });
  const front = vehicleFrontCanvas({
    w: 128,
    h: 96,
    paint: yellow,
    screens: [{ top: 0.12, height: 0.34, split: 2 }],
    lamps: { y: 0.68, r: 0.07, spread: 0.6 },
    grille: { y: 0.62, h: 0.12 },
    bumper: { h: 0.14 },
    grime: 0.4,
    seed: 22,
  });
  const rear = vehicleFrontCanvas({
    w: 128,
    h: 96,
    paint: yellow,
    screens: [{ top: 0.14, height: 0.3, split: 1, inset: 0.14 }],
    lamps: { y: 0.7, r: 0.06, spread: 0.62, color: '#ff3540' },
    bumper: { h: 0.14 },
    grime: 0.6,
    seed: 23,
  });
  const roof = vehicleRoofCanvas({ w: 64, h: 160, paint: '#d3a812', grime: 0.5, seams: 2, seed: 24 });
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(2.1, 1.62, 4.6),
    paintedBoxMaterials({ side, front, rear, roof, roughness: 0.62, emissiveIntensity: 0.4 }),
  );
  body.position.y = 1.13;
  rig.root.add(body);
  rig.root.add(
    box(2.2, 0.14, 0.26, k.chrome, 0, 0.36, 2.34), // chrome bumpers
    box(2.2, 0.14, 0.26, k.chrome, 0, 0.36, -2.34),
    box(0.36, 0.13, 0.06, k.headlamp, -0.62, 1.24, 2.32), // 3D lamps for glow
    box(0.36, 0.13, 0.06, k.headlamp, 0.62, 1.24, 2.32),
    box(0.36, 0.13, 0.06, k.taillamp, -0.62, 1.24, -2.32),
    box(0.36, 0.13, 0.06, k.taillamp, 0.62, 1.24, -2.32),
  );
  // Roof rack: rails on posts + strapped market bundles.
  const rail = std(0x2a2d33, 0.7);
  for (const sx of [-0.8, 0.8]) rig.root.add(box(0.06, 0.06, 3.4, rail, sx, 2.12, -0.1));
  for (const sz of [-1.6, 1.4])
    for (const sx of [-0.8, 0.8]) rig.root.add(box(0.05, 0.16, 0.05, rail, sx, 2.0, sz));
  const bundleCols = [0x3f6c8e, 0x7c5a34, 0x5e7042];
  bundleCols.forEach((col, i) => {
    const b = box(
      1.2 - art(i) * 0.3,
      0.3 + art(i + 3) * 0.14,
      0.86,
      std(col, 0.9),
      (art(i + 7) - 0.5) * 0.3,
      2.3,
      1.0 - i * 1.05,
    );
    b.rotation.y = (art(i + 11) - 0.5) * 0.18;
    rig.root.add(b);
  });
  addWheel(rig, -1.02, 1.5, 0.4, 0.3, 0x8d9299);
  addWheel(rig, 1.02, 1.5, 0.4, 0.3, 0x8d9299);
  addWheel(rig, -1.02, -1.5, 0.4, 0.3, 0x8d9299);
  addWheel(rig, 1.02, -1.5, 0.4, 0.3, 0x8d9299);
  addShadow(rig, 3.0, 5.4);
  swapInModel(rig, DANFO);
  return rig;
}

function buildRickshaw(): MoverRig {
  const k = (kit ??= makeKit());
  const rig = newRig();
  // Mumbai auto-rickshaw: black tub, YELLOW rounded canopy arcing over the
  // cabin, rounded nose cowl with one round headlamp, three exposed wheels.
  const yellowMat = std(0xf2c020, 0.55);
  const black = std(0x1c1e24, 0.7);
  const tub = new THREE.Mesh(roundedBoxGeo(1.3, 0.56, 2.1, 0.1), black);
  tub.position.set(0, 0.58, -0.15);
  rig.root.add(tub);
  // Rounded nose cowl — tucked into the tub front so it reads as one hull.
  // GS4 finish: the ball alone read as a loose ball resting against the tub
  // (dark void between its crown and the windshield) — it now rides HIGHER so
  // its crown meets the glass line, and a rounded DASH BRIDGE in the same
  // yellow seals nose → windshield → tub into one continuous cowl.
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.5, 12, 9), yellowMat);
  nose.scale.set(1.15, 0.95, 0.85);
  nose.position.set(0, 0.78, 0.78);
  rig.root.add(nose);
  const dash = new THREE.Mesh(roundedBoxGeo(1.06, 0.36, 0.7, 0.08), yellowMat);
  dash.position.set(0, 0.94, 0.62);
  rig.root.add(dash);
  // Lamp seats ON the cowl surface (ring back face flush with the sphere —
  // any forward gap reads as a floating lamp at closeup).
  const lampRing = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.05, 10), k.chrome);
  lampRing.rotation.x = Math.PI / 2;
  lampRing.position.set(0, 0.86, 1.22);
  const lampBulb = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 6), k.headlamp);
  lampBulb.position.set(0, 0.86, 1.26);
  rig.root.add(lampRing, lampBulb);
  // Windshield + frame.
  const shield = new THREE.Mesh(
    new THREE.PlaneGeometry(0.92, 0.52),
    new THREE.MeshStandardMaterial({ color: 0x1a2028, roughness: 0.16, side: THREE.DoubleSide }),
  );
  shield.rotation.x = -0.16;
  shield.position.set(0, 1.34, 0.82);
  rig.root.add(shield);
  for (const sx of [-1, 1]) rig.root.add(box(0.05, 0.56, 0.05, black, sx * 0.49, 1.32, 0.84));
  rig.root.add(box(1.02, 0.05, 0.05, black, 0, 1.6, 0.8));
  // The rounded canopy: half-cylinder shell over the cabin (DoubleSide).
  const canopyGeo = new THREE.CylinderGeometry(0.74, 0.74, 2.1, 10, 1, true, Math.PI / 2, Math.PI);
  canopyGeo.rotateX(Math.PI / 2);
  const canopy = new THREE.Mesh(
    canopyGeo,
    new THREE.MeshStandardMaterial({
      color: 0xf2c020,
      roughness: 0.6,
      side: THREE.DoubleSide,
      flatShading: false,
    }),
  );
  canopy.scale.set(0.98, 0.8, 1);
  canopy.position.set(0, 1.14, -0.25);
  rig.root.add(canopy);
  // Rear taillamp.
  rig.root.add(box(0.3, 0.12, 0.06, k.taillamp, 0, 0.8, -1.22));
  // Three EXPOSED wheels: rears sit outside the tub, front under the nose.
  addWheel(rig, 0, 1.05, 0.3, 0.16, 0x8d9299);
  addWheel(rig, -0.74, -0.65, 0.3, 0.16, 0x8d9299);
  addWheel(rig, 0.74, -0.65, 0.3, 0.16, 0x8d9299);
  // Little rear fenders over the exposed wheels (top arc: theta 0.1π..0.9π
  // puts the shell ABOVE the axle after the rotateZ swings the axis onto X).
  // Radius/height tightened so the arc HUGS the tyre instead of hovering.
  for (const sx of [-1, 1]) {
    const fGeo = new THREE.CylinderGeometry(0.33, 0.33, 0.18, 8, 1, true, Math.PI * 0.1, Math.PI * 0.8);
    fGeo.rotateZ(Math.PI / 2);
    const fender = new THREE.Mesh(
      fGeo,
      new THREE.MeshStandardMaterial({ color: 0xf2c020, roughness: 0.6, side: THREE.DoubleSide }),
    );
    fender.position.set(sx * 0.74, 0.36, -0.65);
    rig.root.add(fender);
  }
  addShadow(rig, 2.3, 3.2);
  swapInModel(rig, RICKSHAW);
  return rig;
}

function buildVespa(index: number): MoverRig {
  const rig = newRig();
  // The shared propKit Vespa (leg shield, saddle, chrome mirrors, round
  // headlamp, real wheels, helmeted rider) — the Napoli obstacle reuses the
  // same build parked. Alternate classic pastels per mover.
  const body = index % 2 === 0 ? 0x9fd8c4 : 0xe8e0d0;
  const vespa = buildVespaProp({ body, rider: true });
  rig.root.add(vespa.group);
  for (const w of vespa.wheels) rig.wheels.push({ mesh: w.pivot, radius: w.radius });
  addShadow(rig, 1.3, 2.2);
  // M158: the hand-built scooter replaces the stand-in body; the rider stays.
  attachPropModel(vespa.group, vespaStyle(body), (wheels) => {
    rig.wheels.length = 0;
    rig.wheels.push(...wheels);
  });
  return rig;
}

function buildBeachBall(radius: number): MoverRig {
  const rig = newRig();
  const ball = new THREE.Mesh(
    new THREE.SphereGeometry(radius, 18, 14),
    new THREE.MeshStandardMaterial({ map: beachBallTexture(), roughness: 0.35 }),
  );
  ball.position.y = radius;
  rig.root.add(ball);
  rig.ball = ball;
  rig.ballRadius = radius;
  addShadow(rig, radius * 2.4, radius * 2.4);
  return rig;
}

/** Shared unit sphere: every animal body part is a scaled ellipsoid of it. */
let blobGeo: THREE.SphereGeometry | null = null;

/** One quadruped (goat or guanaco), M158 rebuild: the body is three
 *  overlapping ellipsoids (deep chest, barrel, rump) over a pale belly, the
 *  legs are jointed (thigh, cannon, dark hoof) on hip pivots, the neck rises
 *  from the withers to a shaped head (muzzle, nose, eyes, ears). Goats wear a
 *  beard and curved horns; guanacos a long upright neck, a grey face and tall
 *  ears. The walk rig is unchanged: leg pivots swing in diagonal pairs and
 *  the neck group bobs. */
function quadruped(
  rig: MoverRig,
  opts: {
    x: number;
    z: number;
    scale: number;
    /** Coat + belly base colours (jittered per animal via `jitterIdx`). */
    coat: number;
    belly: number;
    jitterIdx: number;
    neckLen: number; // guanacos hold their necks high; goats barely
    horns: boolean; // true = a goat
    phase: number; // gait offset so a herd doesn't march in lockstep
  },
): void {
  const g = new THREE.Group();
  const s = opts.scale;
  const goat = opts.horns;
  const fur = furMottleTexture();
  const coat = new THREE.MeshStandardMaterial({
    color: toneJitter(opts.coat, opts.jitterIdx, 0.14),
    map: fur,
    roughness: 0.92,
  });
  const pale = new THREE.MeshStandardMaterial({
    color: toneJitter(opts.belly, opts.jitterIdx + 50, 0.1),
    map: fur,
    roughness: 0.94,
  });
  const face = goat ? coat : new THREE.MeshStandardMaterial({ color: 0x5d5049, map: fur, roughness: 0.9 });
  const dark = smooth(0x231d1a, 0.55);
  const geo = (blobGeo ??= new THREE.SphereGeometry(1, 16, 12));
  const blob = (
    rx: number,
    ry: number,
    rz: number,
    mat: THREE.Material,
    x: number,
    y: number,
    z: number,
    parent: THREE.Object3D = g,
  ): THREE.Mesh => {
    const m = new THREE.Mesh(geo, mat);
    m.scale.set(rx * s, ry * s, rz * s);
    m.position.set(x * s, y * s, z * s);
    parent.add(m);
    return m;
  };
  const legLen = 0.62 * s;
  const by = legLen / s + 0.25; // body centre height, in body units
  // (tops kept under the barrel's line so the back reads as one smooth curve)
  blob(0.24, 0.24, 0.52, coat, 0, by, 0); // barrel
  blob(0.225, 0.27, 0.25, coat, 0, by - 0.03, 0.31); // deep chest
  blob(0.23, 0.235, 0.24, coat, 0, by - 0.005, -0.32); // rump
  blob(0.19, 0.14, 0.42, pale, 0, by - 0.13, 0.03); // pale belly
  // Tail: a goat's flicks up, a guanaco's is a short fluffy droop.
  if (goat) {
    const tail = new THREE.Mesh(taperedGeo(0.012 * s, 0.035 * s, 0.16 * s, 5), coat);
    tail.position.set(0, (by + 0.2) * s, -0.56 * s);
    tail.rotation.x = -0.6;
    g.add(tail);
  } else {
    blob(0.06, 0.12, 0.06, coat, 0, by - 0.02, -0.57);
  }
  // Neck + head bob together from a pivot at the withers.
  const neck = new THREE.Group();
  neck.position.set(0, (by + 0.13) * s, 0.36 * s);
  const tilt = goat ? 0.75 : 0.22; // forward lean from vertical
  const L = (opts.neckLen + 0.16) * s;
  const lean = new THREE.Group();
  lean.rotation.x = tilt;
  neck.add(lean);
  const neckMesh = new THREE.Mesh(taperedGeo(goat ? 0.085 * s : 0.075 * s, 0.14 * s, L, 10), coat);
  neckMesh.position.y = L / 2;
  lean.add(neckMesh);
  const head = new THREE.Group();
  head.position.y = L;
  head.rotation.x = -tilt + (goat ? 0.35 : 0.2); // level, nose dipped
  lean.add(head);
  if (!goat) blob(0.085, 0.09, 0.09, coat, 0, -0.01, -0.02, head); // where neck meets skull
  blob(goat ? 0.1 : 0.085, goat ? 0.1 : 0.09, goat ? 0.15 : 0.14, face, 0, 0.02, 0.07, head); // skull
  blob(goat ? 0.065 : 0.055, goat ? 0.06 : 0.055, goat ? 0.1 : 0.09, face, 0, -0.02, 0.2, head); // muzzle
  blob(0.035, 0.025, 0.02, dark, 0, -0.01, 0.285, head); // nose
  for (const ex of [-1, 1]) {
    blob(0.018, 0.018, 0.012, dark, ex * 0.07, 0.06, 0.13, head); // eye
    const ear = new THREE.Mesh(earFinGeo(0.045 * s, (goat ? 0.15 : 0.19) * s), goat ? coat : face);
    ear.position.set(ex * 0.07 * s, 0.11 * s, 0.02 * s);
    // Goat ears flop out and down; guanaco ears stand tall, tipped back.
    ear.rotation.set(goat ? 0.2 : -0.25, 0, ex * (goat ? -1.9 : -0.25));
    head.add(ear);
  }
  if (goat) {
    // Beard under the chin.
    const beard = new THREE.Mesh(taperedGeo(0.035 * s, 0.006 * s, 0.13 * s, 5), smooth(0x3a332d, 0.9));
    beard.position.set(0, -0.1 * s, 0.17 * s);
    head.add(beard);
    // Horns sweeping up and back in a curve.
    const horn = smooth(0xcfc3aa, 0.6);
    for (const hx of [-1, 1]) {
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(hx * 0.05 * s, 0.09 * s, 0.06 * s),
        new THREE.Vector3(hx * 0.07 * s, 0.2 * s, -0.01 * s),
        new THREE.Vector3(hx * 0.09 * s, 0.23 * s, -0.12 * s),
        new THREE.Vector3(hx * 0.1 * s, 0.17 * s, -0.2 * s),
      ]);
      head.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 10, 0.022 * s, 5), horn));
    }
  }
  g.add(neck);
  rig.bobbers.push(neck);
  // Four jointed legs on hip pivots — diagonal pairs swing together.
  const hips: [number, number, number, boolean][] = [
    [-0.13, 0.36, 1, true],
    [0.13, 0.36, -1, true],
    [-0.14, -0.36, -1, false],
    [0.14, -0.36, 1, false],
  ];
  for (const [hx, hz, sign, front] of hips) {
    const pivot = new THREE.Group();
    pivot.position.set(hx * s, legLen + 0.06 * s, hz * s);
    // Pivot-local heights: the ground is at -(legLen + 0.06 s).
    const ground = -(legLen + 0.06 * s);
    const knee = -legLen * 0.5; // thigh (hip → knee), cannon (knee → hoof), hoof
    const thigh = new THREE.Mesh(taperedGeo((front ? 0.07 : 0.09) * s, 0.048 * s, -knee + 0.06 * s, 8), coat);
    thigh.position.y = (knee + 0.06 * s) / 2;
    const cannonTop = knee + 0.02 * s;
    const cannonBot = ground + 0.065 * s;
    const cannon = new THREE.Mesh(taperedGeo(0.042 * s, 0.032 * s, cannonTop - cannonBot, 6), pale);
    cannon.position.y = (cannonTop + cannonBot) / 2;
    const hoof = new THREE.Mesh(taperedGeo(0.034 * s, 0.042 * s, 0.07 * s, 6), dark);
    hoof.position.y = ground + 0.035 * s;
    pivot.add(thigh, cannon, hoof);
    g.add(pivot);
    rig.legs.push({ pivot, sign: sign * (opts.phase % 2 === 0 ? 1 : -1) });
  }
  g.position.set(opts.x, 0, opts.z);
  rig.root.add(g);
}

function buildGoats(): MoverRig {
  const rig = newRig();
  const coats: [number, number][] = [
    [0xe8e2d4, 0xf4f0e6],
    [0xa8886a, 0xd8c8b0],
    [0x8a8078, 0xc8beb2],
  ];
  // A little single-file line along the travel axis (+Z leads the crossing).
  coats.forEach(([c, b], i) => {
    quadruped(rig, {
      x: (art(i * 7) - 0.5) * 0.7,
      z: 1.7 - i * 1.7,
      scale: 0.72 * (0.94 + art(i * 13 + 2) * 0.14), // slight per-goat size variance
      coat: c,
      belly: b,
      jitterIdx: i * 17 + 3,
      neckLen: 0.34,
      horns: true,
      phase: i,
    });
  });
  addShadow(rig, 2.2, 6.0);
  return rig;
}

function buildGuanacos(): MoverRig {
  const rig = newRig();
  for (let i = 0; i < 2; i++) {
    quadruped(rig, {
      x: (i - 0.5) * 1.3,
      z: (0.5 - i) * 1.6,
      scale: 1.0 * (0.96 + art(i * 29 + 5) * 0.1),
      coat: 0xc49a6a,
      belly: 0xefe6d4,
      jitterIdx: i * 23 + 7,
      neckLen: 0.85, // the long guanaco neck is the silhouette
      horns: false,
      phase: i,
    });
  }
  addShadow(rig, 2.6, 4.2);
  return rig;
}

export function buildRig(style: string, radius: number, index: number): MoverRig {
  switch (style) {
    case 'cab':
      return buildCab();
    case 'keicab':
      return buildKeicab();
    case 'bus':
      return buildBus();
    case 'danfo':
      return buildDanfo();
    case 'rickshaw':
      return buildRickshaw();
    case 'vespa':
      return buildVespa(index);
    case 'beachball':
      return buildBeachBall(radius);
    case 'goats':
      return buildGoats();
    case 'guanaco':
      return buildGuanacos();
    default:
      return buildCab();
  }
}

export class MoversView {
  readonly group = new THREE.Group();
  private rigs: MoverRig[] = [];
  private builtFor: readonly PlacedMover[] | null = null;
  private lastLat: number[] = [];
  private spin: number[] = [];

  /** Pose every mover from the sim's timetable. Rebuilds the meshes when the
   *  mover set changes (new city). `raceTime` is the snapshotted race clock. */
  update(track: Track, movers: readonly PlacedMover[], raceTime: number): void {
    if (this.builtFor !== movers) this.rebuild(movers);
    this.group.visible = movers.length > 0; // re-shows after a between-races hide()
    for (let i = 0; i < movers.length; i++) {
      const m = movers[i];
      const rig = this.rigs[i];
      const pose = moverPoseAt(m, raceTime);
      const x = m.x + m.rightX * pose.lat;
      const z = m.z + m.rightZ * pose.lat;
      rig.root.position.set(x, track.heightAt(x, z), z);
      // Face the travel direction (held through the dwell): the crossing axis.
      rig.root.rotation.y = Math.atan2(m.rightX * pose.facing, m.rightZ * pose.facing);
      // Distance-driven animation: wheels/legs/roll all freeze while parked.
      const dLat = pose.lat - (this.lastLat[i] ?? pose.lat);
      this.lastLat[i] = pose.lat;
      this.spin[i] = (this.spin[i] ?? 0) + Math.abs(dLat);
      const travel = this.spin[i];
      for (const w of rig.wheels) w.mesh.rotation.x = travel / w.radius;
      for (const l of rig.legs) l.pivot.rotation.x = Math.sin(travel * 3.2) * 0.5 * l.sign;
      for (const b of rig.bobbers) b.rotation.x = Math.sin(travel * 3.2 + 1.2) * 0.07;
      if (rig.ball && rig.ballRadius) {
        // The beach ball ROLLS about its axle and takes light bounding hops.
        rig.ball.rotation.x = travel / rig.ballRadius;
        const hop = Math.abs(Math.sin(travel * 0.9)) * (pose.moving !== 0 ? 0.9 : 0);
        rig.ball.position.y = rig.ballRadius + hop;
        const squash = pose.moving !== 0 && hop < 0.1 ? 0.88 : 1;
        rig.ball.scale.set(1, squash, 1);
      }
    }
  }

  hide(): void {
    this.group.visible = false;
  }

  private rebuild(movers: readonly PlacedMover[]): void {
    this.disposeRigs();
    this.rigs = movers.map((m, i) => {
      const rig = buildRig(m.style, m.radius, i);
      this.group.add(rig.root);
      return rig;
    });
    this.builtFor = movers;
    this.lastLat = [];
    this.spin = [];
    this.group.visible = movers.length > 0;
  }

  private disposeRigs(): void {
    for (const rig of this.rigs) {
      this.group.remove(rig.root);
      rig.root.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
      });
    }
    this.rigs = [];
  }

  dispose(): void {
    this.disposeRigs();
  }
}
